/*
 * The shared alert chrome every Alert inherits through the theme Alert defaults.
 * Set on the theme (not per call site) so an info/warning/error notice — a form
 * error, a billing banner, a studio nudge — all read the same calm, designed
 * callout without any call site restating it: a soft tinted surface (the default
 * `light` variant paints `--alert-bg` from the color's `*-light` token), a hairline
 * border in the same hue, a rounded icon chip anchoring the notice, an accent-colored
 * semibold title, and a readable detail body. Theme-level classNames only resolve
 * under the real buildTheme, so a bare MantineProvider in a test keeps Mantine's
 * stock chrome.
 *
 * The framed treatment is scoped to the tinted variants via :not([data-variant="filled"]):
 * the `filled` variant paints a solid color surface with white text and owns its own
 * contrast, so the border + chip + recolored title would fight it — the two map alert /
 * search filled notices keep Mantine's stock filled chrome untouched.
 */

/*
 * root frames the notice as a contained callout on the tinted variants: a hairline
 * border in the alert's own hue (a soft mix of --alert-color, which resolves to the
 * color's light-variant text token and tracks the intent — amber for a warning, red
 * for an error, blue for an info) around the light `--alert-bg` surface, with generous
 * padding. This reads as a deliberate designed callout rather than a bare tinted strip.
 */
.root:not([data-variant="filled"]) {
  padding: var(--mantine-spacing-md);
  border: 1px solid color-mix(in srgb, var(--alert-color) 22%, transparent);
}

/*
 * icon seats the notice glyph in a rounded chip tinted with the alert color, so every
 * notice carries one strong, consistent anchor keyed off --alert-color. The fixed
 * square centers the glyph regardless of its own size; align-self pins it to the title
 * line. Scoped to the tinted variants so the filled variant keeps its plain glyph.
 */
.root:not([data-variant="filled"]) .icon {
  display: flex;
  align-items: center;
  align-self: flex-start;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  color: var(--alert-color);
  background-color: color-mix(in srgb, var(--alert-color) 14%, transparent);
  border-radius: var(--mantine-radius-sm);
}

/*
 * title is the one weight + size every notice heading shares: semibold at a calm sm
 * size, colored by Mantine's `light` variant with the alert color. The bottom margin
 * opens a calm gap to the detail body when both are present.
 */
.title {
  margin-bottom: var(--mantine-spacing-xxs);
  font-size: var(--mantine-font-size-xs);
  font-weight: 600;
}

/*
 * message is the notice body, read at a calm sm size in the regular text color (not
 * dimmed) so a message-only notice — a form error, a shown-once warning — reads
 * clearly as the primary content, while a titled notice's detail still sits secondary
 * to the bold accent title. Scoped to the tinted variants; the filled variant keeps
 * its own white text.
 */
.root:not([data-variant="filled"]) .message {
  font-size: var(--mantine-font-size-xs);
  line-height: 1.5;
  color: var(--mantine-color-text);
}
